import { CreateTemplate, ResponseTemplate, TemplateService, UpdateTemplate } from "@/services/templates/templates.service";
import { defineStore } from "pinia";
import { useAuthStoreUser } from "./authUser";
import { IdConta, IdTemplate, makeIdConta, makeIdTemplate, makeIdUsuario } from "@/services/common-types";

export const useTemplateStore = defineStore('template', {
    state: () => ({
        initialized: false as boolean,
        idConta: undefined as IdConta|undefined,
        templates: [] as ResponseTemplate[],
        service: new TemplateService(),
    }),
    actions: {
        getDefault() {
            const defaultTemplate = {
                idConta: '',
                idUsuario: '',
                legenda: '',
                mensagem: '',
                nome: '',
                conteudo: 'TEXTO',
                visibilidade: 'PESSOAL',
                chavePix: '',
                codigoSenha: '',
                textoBotao: '',
                tipoChavePix: null,
                del: false,
                id: makeIdTemplate(''),
            } as ResponseTemplate;
            return {...defaultTemplate};
        },
        async tryToGetConta() {
            const userAuthStore = useAuthStoreUser();
            await userAuthStore.init();
            const contasUsuario = Object.keys(userAuthStore.contas);
            const hopefullyOnlyConta = contasUsuario.at(0);
            if (contasUsuario.length != 1) throw new Error(`[TemplateStore] Store inicializada sem ID de usuário, e não é possível inferir o ID da conta pois há ${contasUsuario.length} contas vinculadas a esse usuário.`);
            if (!hopefullyOnlyConta) throw new Error(`[TemplateSrote] Store inicializada sem ID de usuário, e a inferência automática de contas não encontrou nenhuma conta associada.`)
            return makeIdConta(hopefullyOnlyConta);
        },
        async init(idConta?: IdConta) {
            if (this.initialized) return;

            if (idConta) this.idConta = idConta;
            if (!this.idConta) {
                this.idConta = await this.tryToGetConta();
            }

            this.templates = await this.service.getAllByConta(this.idConta);

            this.initialized = true;
            return this;
        },
        async reloadForConta(idConta: IdConta) {

            this.idConta = idConta;
            this.templates = await this.service.getAllByConta(idConta);

            return this;
        },
        async getAll(search?: string) {
            if (!this.initialized) return [];
            if (!this.idConta) throw new Error('[templateStore] [getAll] `this.idConta` was `undefined`');
            const res = await this.service.getAllByConta(this.idConta, search);
            this.templates = res;
            return res;
        },
        async getById(search: string) {
            await this.init();
            return await this.service.getById(search);
        },
        async create(template: CreateTemplate) {
            await this.init();
            const res = await this.service.create(template);
            await this.getAll();
            return res;
        },
        async update(template: UpdateTemplate) {
            await this.init();
            const res = await this.service.update(template);
            await this.getAll();
            return res;
        },
        async delete(idTemplate: string) {
            await this.init();
            const res = await this.service.delete(idTemplate);
            await this.getAll();
            return res;
        }
    }
});

export const useMultiTemplateStore = defineStore('manyTemplates', {
    state: () => ({
        initialized: false as boolean,
        contas: [] as IdConta[],
        contasTemplatesMap: new Map() as Map<IdConta, ResponseTemplate[]>,
        service: new TemplateService(),
    }),
    getters: {
        templates(): ResponseTemplate[] {
            if (!this.initialized) return [];
            return Array.from(this.contasTemplatesMap.values()).flat();
        }
    },
    actions: {
        getDefault() {
            const defaultTemplate = {
                idConta: makeIdConta(''),
                idUsuario: makeIdUsuario(''),
                legenda: '',
                mensagem: '',
                nome: '',
                conteudo: 'TEXTO',
                visibilidade: 'PESSOAL',
                chavePix: '',
                codigoSenha: '',
                textoBotao: '',
                tipoChavePix: null,
                nomeContaPix: null,
                del: false,
                id: makeIdTemplate(''),
                idInbox: null,
                contas: [],
                inboxes: [],
            } as CreateTemplate;
            return {...defaultTemplate};
        },
        async init(idContas: IdConta|IdConta[]) {
            if (this.initialized) return;

            if (!Array.isArray(idContas)) idContas = [idContas];

            await this.getByContas(idContas);

            this.initialized = true;
        },
        async getByContas(idContas: IdConta[]) {
            const contaToTemplates = await Promise.all(idContas.map(async idConta => ({idConta, templates: await this.getByConta(idConta)})));
            contaToTemplates.forEach(ctt => this.contasTemplatesMap.set(ctt.idConta, ctt.templates))
            return contaToTemplates.flat();
        },
        async getByConta(idConta: IdConta) {
            const templates = await this.service.getAllByConta(idConta);
            this.contasTemplatesMap.set(idConta, templates);
            return templates;
        },
        async create(template: CreateTemplate) {
            const res = await this.service.create(template);
            await this.getByConta(template.idConta);
            return res;
        },
        async update(template: UpdateTemplate) {
            const res = await this.service.update(template);
            if (template.idConta) 
                await this.getByConta(template.idConta);
            return res;
        },
        async delete(idTemplate: IdTemplate, idConta: IdConta) {
            const res = await this.service.delete(idTemplate);
            await this.getByConta(idConta);
            return res;
        },
    }
})